import React, { memo, useMemo } from 'react'
import Card from 'transfix-ui/components/Card'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/graphqlTypes'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import useUserDetails from 'utils/hooks/useUserDetails'
import { formatRate as formatRateUtil } from 'utils/formatRate'
import CarrierOptionRateCardRow from './components/CarrierOptionRateCardRow'
import CommissionTiersSection from './components/CommissionTiersSection'
import { RateCoachCardRow } from './components/RateCoachCardRow'
import { TEST_IDS } from './constants'
import { useShipmentRates, useCarrierLaneHistory } from './hooks'

const useCarrierOptionRateCardStyles = makeStyles(theme => ({
  card: {
    '& .flex': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  header: {
    marginBottom: theme.spacing(0.5),
  },
  margin: {
    marginBottom: theme.spacing(3),
    '&.positiveMargin': {
      color: brandColors.green3,
    },
    '&.negativeMargin': {
      color: brandColors.error1,
    },
  },
  counterOfferContainer: {
    marginBottom: theme.spacing(2.5),
  },
  rate: {
    marginBottom: theme.spacing(1),
  },
  history: {
    marginBottom: theme.spacing(2.5),
  },
}))

const formatRate = (rate: Maybe<number>) => {
  return rate ? formatRateUtil(rate) : null
}
export interface ICarrierOptionRateCardProps {
  shipmentId: Maybe<string>
  carrierApplicationId: Maybe<string>
  rate: Maybe<number>
  children?: JSX.Element
  counterOfferComponent?: JSX.Element
}

const CarrierOptionRateCard = memo<ICarrierOptionRateCardProps>(
  ({ carrierApplicationId, children, counterOfferComponent, rate, shipmentId }) => {
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isCarrierCompIncentiveEnabled } = getFlag('enableCarrierCompIncentive')

    const { userPermissions } = useUserDetails()
    const { canViewCmTargetRates } = userPermissions || {}

    const classes = useCarrierOptionRateCardStyles()

    const {
      commissionTierBands,
      listRate,
      loading: ratesLoading,
      percentToMarket,
      rateCoachGrade,
      repTarget,
      shipperRate,
    } = useShipmentRates({ shipmentId, rate })

    const { carrierRateHistory, loading: historyLoading } = useCarrierLaneHistory({
      carrierApplicationId,
      shipmentId,
    })

    const margin = useMemo(() => {
      if (rate && shipperRate) {
        return shipperRate - rate
      }
      return null
    }, [rate, shipperRate])

    const isMarginNegative = Boolean(margin && margin < 0)

    const marginClass = isMarginNegative ? 'negativeMargin' : 'positiveMargin'
    const formattedMargin = useMemo(() => {
      if (!margin) {
        return null
      }
      return isMarginNegative ? `- ${formatRateUtil(margin * -1)}` : `+ ${formatRateUtil(margin)}`
    }, [isMarginNegative, margin])

    const formattedPercentToMarket = useMemo(() => {
      if (!percentToMarket) {
        return null
      }
      return `${(percentToMarket * 100).toFixed(2)}%`
    }, [percentToMarket])

    return (
      <Card className={classes.card}>
        <CarrierOptionRateCardRow
          title='Rate'
          typographyVariant='h3'
          className={classes.header}
          value={formatRate(rate)}
          dataTest={TEST_IDS.rate}
        />
        <CarrierOptionRateCardRow
          title='Margin (Estimated)'
          typographyVariant='subtitle2'
          color='textSecondary'
          className={`${classes.margin} ${marginClass}`}
          value={formattedMargin}
          valueColor='inherit'
          loading={ratesLoading}
          dataTest={TEST_IDS.margin}
        />
        <div className={classes.margin}>
          <RateCoachCardRow rateCoachGrade={rateCoachGrade} />
        </div>
        {counterOfferComponent && (
          <div className={classes.counterOfferContainer}>{counterOfferComponent}</div>
        )}
        <CarrierOptionRateCardRow
          title='List Rate'
          className={classes.rate}
          value={formatRate(listRate)}
          loading={ratesLoading}
          dataTest={TEST_IDS.listRate}
        />
        {canViewCmTargetRates && (
          <CarrierOptionRateCardRow
            title='Rep Target'
            className={classes.rate}
            value={formatRate(repTarget)}
            loading={ratesLoading}
            dataTest={TEST_IDS.repTarget}
          />
        )}
        <CarrierOptionRateCardRow
          title='Shipper Rate'
          className={classes.rate}
          value={formatRate(shipperRate)}
          loading={ratesLoading}
          dataTest={TEST_IDS.shipperRate}
        />
        <hr />
        <CarrierOptionRateCardRow
          title='Percent to Market'
          className={classes.history}
          value={formattedPercentToMarket}
          loading={ratesLoading}
          dataTest={TEST_IDS.percentToMarket}
        />
        <CarrierOptionRateCardRow
          title='Carrier Rate History'
          value={carrierRateHistory}
          loading={historyLoading}
          dataTest={TEST_IDS.carrierRateHistory}
        />
        {isCarrierCompIncentiveEnabled && (
          <>
            <hr />
            <CommissionTiersSection
              commissionTierBands={commissionTierBands}
              loading={ratesLoading}
              rate={rate}
            />
          </>
        )}
        {children}
      </Card>
    )
  }
)

export default CarrierOptionRateCard
